import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import getConfig from 'next/config';
import { DatePickerCalendar, DateRangePicker } from '@finn-no/legacy-fabric-datepicker-nopopover';
import { Alert, Button } from '@fabric-ds/react';

import { formatClientDate, formatISODate, formatYMDDate, isPast, safeDifferenceInCalendarDays } from 'shared/util/datetime-fns';
import { trackCheckPriceAndAvailability, trackSendMessage } from '@/lib/tracking/tracking';
import { isImportBrandEnabled, isNextPaxBrandEnabled } from '@/lib/helpers/importBrands';
import { replacePropsWithPreview } from '../../utils';

import { Availability } from '../availability/Availability';

import styles from './styles.module.css';
import { differenceInCalendarDays } from 'date-fns';

const {
    publicRuntimeConfig: { contextPath },
} = getConfig();

const disclaimer = 'FINN.no forbeholder seg retten til å kontrollere meldingene og stoppe useriøs e-post.';

export function Calendar({ loginCookieCsrfToken, isLoggedIn, unleash }) {
    const filters = useSelector((state) => state.results.filters);
    const objectData = useSelector((state) => state.objectData);

    const { adId, bookings, isAdmin, isImport, owner, user } = objectData;
    const isPreview = Boolean(objectData.isPreview);
    const bookingEnabled = Boolean(objectData.bookingEnabled);

    const [startDate, setStartDate] = useState(filters?.booking_from ? new Date(filters.booking_from) : null);
    const [tooFewDaysSelected, setTooFewDaysSelected] = useState(false);
    const [endDate, setEndDate] = useState(filters?.booking_to ? new Date(filters.booking_to) : null);

    const [deepLinkParams, setDeepLink] = useState('');
    const [isContactFormVisible, setIsContactFormVisible] = useState(Boolean(startDate && endDate));
    const [bookingPeriod, setBookingPeriod] = useState('');

    if (!bookingEnabled) {
        return null;
    }

    const handleDateSelect = (dates) => {
        if (isPreview) {
            return;
        }

        setStartDate(dates.startDate);
        setEndDate(dates.endDate);

        if (dates.startDate && dates.endDate) {
            setTooFewDaysSelected(
                objectData?.priceInfo?.minimumOneWeekRental && differenceInCalendarDays(dates.endDate, dates.startDate) < 6
            );
            setIsContactFormVisible(true);
            setDeepLink(generatePeriodParamsForUrl(dates.startDate, dates.endDate));
            setBookingPeriod(`${formatClientDate(dates.startDate)} - ${formatClientDate(dates.endDate)}`);
        } else {
            // one or no dates selected
            setTooFewDaysSelected(false);
        }
    };

    const handleDateReset = () => {
        setBookingPeriod('');
        setDeepLink('');

        setStartDate(null);
        setEndDate(null);
    };

    const trackContactMessageSent = () => {
        trackSendMessage({
            objectName: 'with calendar',
            objectData: objectData,
            fromDate: startDate ? formatISODate(startDate) : null,
            toDate: endDate ? formatISODate(endDate) : null,
        });
    };

    const generatePeriodParamsForUrl = (from, to) => {
        if (!from || !to) {
            return;
        }
        if (owner?.name === 'DanCenter') {
            return `&firstdate=${formatYMDDate(from)}&days=${safeDifferenceInCalendarDays(from, to) ?? 1}`;
        }
        if (owner?.name === 'Novasol') {
            return `&from=${formatYMDDate(from)}&to=${formatYMDDate(to)}`;
        }
        return '';
    };

    const renderImportAdButton = () =>
        owner ? (
            <div>
                <a
                    className="button button--primary w-full max-w-full"
                    onClick={() => trackCheckPriceAndAvailability({ objectData })}
                    href={`${contextPath}/ut?finnkode=${adId}&deepLinkParams=${deepLinkParams !== '' ? btoa(deepLinkParams) : ''}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow external"
                    data-testid="importAdButton">
                    Se pris og tilgjengelighet
                </a>
                {isImport && <div className="text-12 mt-8">OBS! I enkelte perioder kan man kun leie hele uker.</div>}
            </div>
        ) : null;

    const renderForm = () =>
        user ? (
            <form action="/contact.html" method="post" id="contact_form" data-testid="contactForm" onSubmit={trackContactMessageSent}>
                {tooFewDaysSelected ? (
                    <Alert type="info" show>
                        Minimum opphold 6 netter.
                    </Alert>
                ) : null}
                <input type="hidden" name="finnkode" defaultValue={adId || ''} />
                <input type="hidden" name="from_email" id="from_email" defaultValue={user.email} />
                <input type="hidden" name="csrfToken" defaultValue={loginCookieCsrfToken} />
                {isContactFormVisible ? (
                    <div className="mt-16 text-left space-y-16">
                        <div className="input input--text">
                            <label className="required" htmlFor="daterange">
                                Velg periode i kalenderen eller skriv inn her
                            </label>
                            <input
                                type="text"
                                id="daterange"
                                value={bookingPeriod}
                                name="extraParams[date_range]"
                                placeholder="DD.MM.YYYY - DD.MM.YYYY"
                                onChange={(event) => setBookingPeriod(event.target.value)}
                                data-testid="contactDateRange"
                            />
                        </div>
                        <div className="input input--textarea">
                            <label className="required" htmlFor="message">
                                Melding til utleier
                            </label>
                            <textarea
                                rows="5"
                                name="message"
                                id="message"
                                placeholder="Ta gjerne med antall personer"
                                required
                                data-testid="contactMessage"
                            />
                        </div>
                        <div className="input input--text input--is-read-only">
                            <label className="required" htmlFor="from_email">
                                E-post
                            </label>
                            <input
                                type="email"
                                name="from_email"
                                id="from_email"
                                required
                                disabled
                                defaultValue={user.email}
                                data-testid="contactEmail"
                            />
                            {user.email && (
                                <a className="text-14" href="/min-finn/min-konto">
                                    Du kan endre denne e-postadressen i profilen din
                                </a>
                            )}
                        </div>
                        <div className="input input--text">
                            <label className="required" htmlFor="phone_number">
                                Telefonnummer
                            </label>
                            <input
                                type="text"
                                name="extraParams[phone_number]"
                                id="phone_number"
                                defaultValue={user.phone}
                                placeholder="Ta gjerne med landskode"
                                data-testid="contactPhoneNumber"
                            />
                        </div>
                        <div className="input">
                            <button className="button button--primary mb-16" type="submit" id="submit">
                                Send melding
                            </button>
                            <button
                                className="button button--tertiary mb-16 ml-16"
                                type="button"
                                id="cancel"
                                onClick={() => setIsContactFormVisible(false)}>
                                Avbryt
                            </button>
                            <div className="text-14 mb-16">{disclaimer}</div>
                        </div>
                    </div>
                ) : (
                    <>
                        <button
                            className="button button--primary my-16 w-full max-w-full"
                            {...replacePropsWithPreview(
                                {
                                    onClick: (e) => {
                                        e.preventDefault();
                                        setIsContactFormVisible(true);
                                    },
                                },
                                isPreview
                            )}>
                            Skriv en melding til utleier
                        </button>
                        <div className="text-14 mb-16">{disclaimer}</div>
                    </>
                )}
            </form>
        ) : null;

    const renderContent = () =>
        isLoggedIn ? (
            renderForm()
        ) : (
            <a className="button button--primary my-16 w-full max-w-full" data-testid="loginLink" href="/auth/login">
                Logg inn for å sende melding
            </a>
        );

    return (
        <section className="space-y-16 my-16" data-testid="adCalendar" aria-labelledby="objectCalendarHeading">
            {isNextPaxBrandEnabled(owner?.name, unleash) ? (
                <h3 className="h4" id="objectCalendarHeading">
                    Finn ledig tid i kalenderen
                </h3>
            ) : (
                <h3 className="h4" id="objectCalendarHeading">
                    Kalender for ledighet
                </h3>
            )}
            <div>
                <DateRangePicker
                    bookedDates={bookings ?? []}
                    startDate={startDate}
                    endDate={endDate}
                    minimumNights={1}
                    isDayDisabled={isPast}
                    onChange={handleDateSelect}>
                    <DatePickerCalendar
                        numberOfMonths={1}
                        className={cn(styles.datePicker, '-mx-32 border lg:border-none')}
                        data-testid="object-calendar"
                    />
                </DateRangePicker>
            </div>
            {startDate && endDate ? (
                <Button link small onClick={handleDateReset} data-testid="object-calendar-reset">
                    Nullstill datoer
                </Button>
            ) : null}
            {isImportBrandEnabled(owner?.name, unleash) ? (
                <Availability
                    adId={adId}
                    startDate={startDate}
                    endDate={endDate}
                    novasolButton={owner?.name === 'Novasol' ? renderImportAdButton() : null}
                />
            ) : isImport || (isAdmin && owner.deeplink) ? (
                renderImportAdButton()
            ) : (
                renderContent()
            )}
        </section>
    );
}

Calendar.propTypes = {
    loginCookieCsrfToken: PropTypes.string,
    isLoggedIn: PropTypes.bool.isRequired,
    unleash: PropTypes.object.isRequired,
};
